import { ChangeDetectorRef, Component, Input, OnInit } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { Observable, takeUntil } from "rxjs";
import { BaseComponent } from "src/app/base.component";
import { Constants } from "src/app/shared/data/constants";
import { FeatureName } from "src/app/shared/enum/feature-name.enum";
import { UserHistoryActionsEnum } from "src/app/shared/enum/userHistoryActions";
import { UserHistoryServicesEnum } from "src/app/shared/enum/userHistoryService";
import { AvailableFeaturesDto } from "src/app/shared/models/features/available-feature-dto.interface";
import { BatchToggleFeatureDto } from "src/app/shared/models/features/batch-toggle-feature-dto.interface";
import { BatchToggleFeatureElementDto } from "src/app/shared/models/features/batch-toggle-feature-element-dto.interface";
import { UserFeatureDto } from "src/app/shared/models/features/user-feature-dto.interface";
import { SuccessDto } from "src/app/shared/models/success-dto";
import { RoleEnum } from "src/app/shared/models/user/role.interface";
import { SaveUserHistoryService } from "src/app/shared/services/history/saveHistory";
import { JwtService } from "src/app/shared/services/jwt.service";
import { AppStateInterface } from "src/app/store/appState.interface";
import { batchToggleFeatureSuccess, getAllFeatures, invokeAllFeatures, invokeBatchToggleFeature } from "src/app/store/features/features.actions";
import { allAvailableFeaturesSelector, allFeaturesSelector, featureSuccessSelector } from "src/app/store/features/features.selectors";

@Component({
  selector: "app-features",
  templateUrl: "./features.component.html",
})
export class FeaturesComponent extends BaseComponent implements OnInit {
    @Input() userEmail: string;
    @Input() companyName: string;
    @Input() isFranchise: boolean | null = false;
    @Input() isFranchised: boolean | null = false;

    title = "";
    today = new Date();
    tomorrow = new Date(this.today.getTime() + Constants.oneDay);
    features: BatchToggleFeatureElementDto[] = [];
    features$: Observable<UserFeatureDto[] | null>;
    availableFeatures: AvailableFeaturesDto[] = [];
    availableFeatures$: Observable<AvailableFeaturesDto[] | null>;
    success$: Observable<SuccessDto | null>;
    
    constructor(
        private store: Store<AppStateInterface>,
        private cd: ChangeDetectorRef,
        private modalService: NgbModal,
        private toaster: ToastrService,
        private translate: TranslateService,
        private jwtService: JwtService,
        private saveHistory : SaveUserHistoryService
    ){

        super(store);

        this.features$ = this.store
            .pipe(select(allFeaturesSelector))
            .pipe(takeUntil(this.ngDestroyed$));

        this.availableFeatures$ = this.store
            .pipe(select(allAvailableFeaturesSelector))
            .pipe(takeUntil(this.ngDestroyed$));

        this.success$ = this.store
            .pipe(select(featureSuccessSelector))
            .pipe(takeUntil(this.ngDestroyed$));
    }


    ngOnInit(): void {
        this.store.dispatch(getAllFeatures({ features: null }));
        this.availableFeatures$.subscribe((result) => {
            if (result) {
                this.availableFeatures = result;
            }
        });
        const currentUserRole = this.jwtService.getRoleName(localStorage.getItem("token"));

        this.features$.subscribe((result) => {
            if (result) {
                this.features = [];
                if (!this.isFranchised) {
                    this.availableFeatures = this.availableFeatures.filter((availableFeature) => availableFeature.name !== FeatureName.REMUNERATION);
                }
                if (this.isFranchised && currentUserRole === RoleEnum.ROLE_FRANCHISE) {
                    this.availableFeatures = this.availableFeatures.filter((availableFeature) => availableFeature.name === FeatureName.REMUNERATION);
                }
                if (this.isFranchise) {
                    this.availableFeatures = this.availableFeatures.filter((availableFeature) => availableFeature.name === FeatureName.PREMIUM);
                }
                this.availableFeatures.forEach((availableFeature) => {
                    const feature: UserFeatureDto | undefined = result.find(currentFeature => availableFeature.name === currentFeature.name);
                    const featureElement: BatchToggleFeatureElementDto = {
                        enable: false,
                        featureName: availableFeature.name,
                        startDate: availableFeature.hasStartDate ? this.today : undefined,
                        endDate: availableFeature.hasEndDate ? this.tomorrow : undefined
                    }
                    if (feature){
                        featureElement.enable = true;
                        featureElement.startDate = availableFeature.hasStartDate ? feature.startDate || this.today : undefined;
                        featureElement.endDate = availableFeature.hasEndDate ? feature.endDate || this.tomorrow : undefined;
                    }
                    this.features.push(featureElement);
                })
            }
            else {
                this.store.dispatch(invokeAllFeatures({ email: this.userEmail }));
            }
        });
        this.success$.subscribe((success) => {
            if (success) {
                this.toaster.success(this.translate.instant("response.success." + success.message));
                this.store.dispatch(batchToggleFeatureSuccess({ success: null }));
            }
        })
    }

    toggleFeature(feature: BatchToggleFeatureElementDto) {
        const featureIndex = this.features.findIndex((tempFeature) => tempFeature.featureName === feature.featureName);

        this.features = this.features.map((feat, index) => (
            index === featureIndex 
            ?
                {
                    featureName: feat.featureName,
                    enable: !feat.enable,
                    startDate: this.availableFeatures[featureIndex].hasStartDate ? (this.features[featureIndex].startDate || this.today) : undefined,
                    endDate: this.availableFeatures[featureIndex].hasEndDate ? (this.features[featureIndex].endDate || this.tomorrow) : undefined
                }
            :
            feat
        ));

        // Detect change in the event loop not in the main thread
        setTimeout(() => this.cd.detectChanges(), 0);
    }

    closeModal() {
        this.modalService.dismissAll();
    }

    updateFeatures() {
        const payload: BatchToggleFeatureDto = {
            userEmail: this.userEmail,
            elements: []
        };
        this.features.forEach((feature) => {
            const payloadElement: BatchToggleFeatureElementDto = {
                enable: feature.enable,
                featureName: feature.featureName,
                startDate: feature.enable ? feature.startDate || undefined : undefined,
                endDate: feature.enable ? feature.endDate || undefined : undefined
            }
            payload.elements.push(payloadElement);
        })
        this.store.dispatch(
            invokeBatchToggleFeature({ payload })
        );
        this.saveHistory.saveUserHistory(
            UserHistoryActionsEnum.PATCH,
            UserHistoryServicesEnum.FEATURES
          );
        this.closeModal();
    }

    setStartDate(feature: BatchToggleFeatureElementDto, newDate: any) {
        const [day, month, year] = newDate.split("/");
        feature.startDate = new Date(year, month - 1, day);
    }

    setEndDate(feature: BatchToggleFeatureElementDto, newDate: any) {
        const [day, month, year] = newDate.split("/");
        feature.endDate = new Date(year, month - 1, day);
    }
}
